/**
 * <multipack-selector>
 * ---------------------
 * Renders a "Pack" option (Single / 2-Pack / 3-Pack …) that maps each
 * (base variant × pack count) to a virtual-bundle variant. Picking a pack:
 *   - swaps the Add-to-Cart variant id (in-place, no reload)
 *   - updates the displayed price
 *   - greys out packs that don't exist for the current size (sparse grid)
 *   - hides the Recharge subscription widget for multipacks (one-time only)
 *
 * It cooperates with the theme's native variant picker (VariantSelects):
 *   - `variant:change` (document)   → the base size changed
 *   - `productInfo:loaded` (document) → native finished its async re-render,
 *      so we re-assert our override to win the race.
 *
 * All DOM lookups are defensive: if an element isn't found we no-op rather
 * than throw, so a normal (non-multipack) PDP is never affected.
 */
class MultipackSelector extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;

    const dataEl = this.querySelector('[data-multipack-data]');
    if (!dataEl) return;
    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[multipack-selector] bad data payload', e);
      return;
    }

    this.buildMap();

    this.currentBaseId = parseInt(this.dataset.currentBase, 10) || this.firstBaseId();
    this.currentPack = 1;

    this.radios = Array.from(this.querySelectorAll('[data-pack-count]'));
    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => this.onPackChange(radio));
    });

    // The native variant picker element (used to restore its rich price markup).
    this.nativeEl = document.querySelector(
      `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
    );

    // React to the native size/variant picker.
    this.onVariantChange = (e) => this.onBaseChange(e);
    this.onProductInfoLoaded = () => this.reassert();
    document.addEventListener('variant:change', this.onVariantChange);
    document.addEventListener('productInfo:loaded', this.onProductInfoLoaded);

    this.updateDisabledStates();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);
    document.removeEventListener('productInfo:loaded', this.onProductInfoLoaded);
  }

  // map[baseVariantId][packCount] = { variant, price, priceText, compareAtText, available, image, isBundle }
  buildMap() {
    this.map = {};
    (this.data.base || []).forEach((b) => {
      this.map[b.id] = this.map[b.id] || {};
      this.map[b.id][1] = {
        variant: b.id,
        price: b.price,
        priceText: b.priceText,
        compareAtText: b.compareAtText,
        available: b.available,
        isBundle: false,
      };
    });
    (this.data.multipacks || []).forEach((m) => {
      this.map[m.base] = this.map[m.base] || {};
      this.map[m.base][m.count] = {
        variant: m.variant,
        price: m.price,
        priceText: m.priceText,
        compareAtText: m.compareAtText,
        available: m.available,
        image: m.image,
        isBundle: true,
      };
    });
  }

  firstBaseId() {
    return this.data.base && this.data.base[0] ? this.data.base[0].id : null;
  }

  entryFor(baseId, count) {
    return this.map[baseId] ? this.map[baseId][count] : undefined;
  }

  // The base size changed via the native picker.
  onBaseChange(e) {
    const variant = e.detail && e.detail.variant;
    if (!variant || !this.map[variant.id]) return; // not one of our base variants
    this.currentBaseId = variant.id;

    // If the current pack doesn't exist for the new size, fall back to Single.
    if (!this.entryFor(this.currentBaseId, this.currentPack)) {
      this.currentPack = 1;
      const single = this.radios.find((r) => parseInt(r.dataset.packCount, 10) === 1);
      if (single) single.checked = true;
    }
    this.updateDisabledStates();
    // Size change: the native picker already re-rendered base price/ATC. We only
    // need to override when a bundle pack is (still) selected.
    this.applySelection(false);
  }

  onPackChange(radio) {
    this.currentPack = parseInt(radio.dataset.packCount, 10);
    this.updateDisabledStates();
    this.applySelection(true); // user changed pack only → we own the update
  }

  // Enable/disable pack chips for the current size (sparse grid).
  updateDisabledStates() {
    this.radios.forEach((radio) => {
      const count = parseInt(radio.dataset.packCount, 10);
      const entry = this.entryFor(this.currentBaseId, count);
      const exists = !!entry;
      const purchasable = exists && entry.available !== false;

      radio.disabled = !purchasable;
      const chip = this.querySelector(`label[for="${radio.id}"]`);
      if (chip) {
        chip.classList.toggle('is-disabled', !exists);
        chip.classList.toggle('is-soldout', exists && !purchasable);
      }
    });
  }

  // Push the current (size × pack) selection into the ATC form, price, and subscription UI.
  // fromPackChange = the user toggled the pack (native picker did NOT re-render), so
  // for Single we must actively restore the native base price.
  applySelection(fromPackChange) {
    const entry = this.entryFor(this.currentBaseId, this.currentPack);
    if (!entry) return;

    if (entry.isBundle) {
      this.applyBundle(entry);
      return;
    }

    // Single (base variant).
    this.setSubscriptionVisible(true);
    if (fromPackChange) {
      this.setCartVariant(entry.variant);
      this.restoreNativePrice();
    }
  }

  applyBundle(entry) {
    this.setCartVariant(entry.variant);
    this.setPrice(entry);
    this.setAvailability(entry.available !== false);
    this.setSubscriptionVisible(false); // bundles can't be subscribed
  }

  // Restore the base variant's price. We set it instantly from our map (no flash of
  // the previous bundle price), then let the native picker re-render for full fidelity
  // (per-unit price, badges) — that async pass overwrites our instant value harmlessly.
  restoreNativePrice() {
    const base = this.entryFor(this.currentBaseId, 1);
    if (base) this.setPrice(base);

    const native = this.nativeEl;
    if (native && typeof native.renderProductInfo === 'function' && native.currentVariant) {
      native.renderProductInfo();
    }
  }

  // After the native picker's async re-render completes, re-assert a bundle override
  // (the native fetch would otherwise leave the base price/variant showing).
  reassert() {
    const entry = this.entryFor(this.currentBaseId, this.currentPack);
    if (entry && entry.isBundle) {
      this.applyBundle(entry);
    } else {
      this.setSubscriptionVisible(true);
    }
  }

  setCartVariant(variantId) {
    if (!variantId) return;
    const inputs = document.querySelectorAll(
      `#product-form-${this.sectionId} input[name="id"], ` +
      `#product-form-installment-${this.sectionId} input[name="id"], ` +
      `#product-form-${this.sectionId}--alt input[name="id"]`
    );
    inputs.forEach((input) => {
      input.value = variantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // Update the amount(s) inside the theme's existing price markup, in place, so the
  // bundle price keeps the exact same font/structure as the native (Single) price.
  setPrice(entry) {
    const priceEl = document.getElementById(`price-${this.sectionId}`);
    if (!priceEl) return;

    // The visible price lives in the regular price money element. Rewriting only its
    // inner amount preserves the theme's price-money / prefix / superscript markup.
    const regularBdi =
      priceEl.querySelector('.price__regular price-money bdi') ||
      priceEl.querySelector('price-money bdi') ||
      priceEl.querySelector('.price-item--regular, .price-item');

    if (regularBdi) {
      regularBdi.innerHTML = this.moneyHTML(entry.priceText);
    } else {
      priceEl.textContent = entry.priceText; // last-resort fallback
    }
  }

  // Format a money string ("$19.98") into the theme's dollar + superscript-cents markup.
  moneyHTML(text) {
    const m = String(text).match(/^(\D*)(\d[\d,]*)(\.\d+)?(.*)$/);
    if (!m) return text;
    const [, prefix = '', dollars = '', cents = '', suffix = ''] = m;
    return (
      (prefix ? `<span class="price__prefix">${prefix}</span>` : '') +
      dollars +
      (cents ? `<sup class="price__suffix">${cents}</sup>` : '') +
      (suffix ? `<span class="price__suffix">${suffix}</span>` : '')
    );
  }

  setAvailability(available) {
    const buttons = document.querySelectorAll(
      `#product-form-${this.sectionId} [name="add"], #product-form-${this.sectionId} .product-form__submit`
    );
    buttons.forEach((btn) => {
      btn.disabled = !available;
      const span = btn.querySelector('span');
      if (span && !available) span.textContent = window.theme?.variantStrings?.soldOut || 'Sold out';
    });
  }

  setSubscriptionVisible(visible) {
    // Recharge widget host lives outside its shadow DOM inside this container.
    const host = document.querySelector(
      `#shopify-section-${this.sectionId} div[id*="recharge_subscriptions_subscription_widget"]`
    ) || document.querySelector(`#shopify-section-${this.sectionId} recharge-subscription-widget`);
    if (!host) return;
    const container = host.closest('[id*="recharge"]') || host;
    container.style.display = visible ? '' : 'none';
  }
}

if (!customElements.get('multipack-selector')) {
  customElements.define('multipack-selector', MultipackSelector);
}
